import { logError } from '../../../common/js/sentry';

import { isWeb } from '../../../common/js/environment-utils';
import { formatEvent } from './event-formatter';
import * as vpaid from '../../../common/js/vpaid/vpaid';
import { resizeAd } from '../../../common/js/vpaid/vpaid';
import {
  generateUrlWithRegion,
  templateMacros,
  updateQueryStringParameters
} from '../../../common/js/url-utils';
import { getTrackingUrlMacros } from './utils';
import VASTClient from '../lib/vast/vast-client';
import VASTError from '../lib/vast/vast-error';
import { installShortcut } from '../../../common/js/shortcut-utils';
import CONFIG from '../../../common/js/config';
import { isFeatureEnable } from '../../../common/js/features-enabling';
import { openOptOut } from '../../../common/components/opt-out/open';
import { preventSpamCallsShared } from '../../../common/js/helpers';
import {
  sendOmidImpression,
  sendOmidMediaEvent
} from '../../../common/js/omid/omid';
import {
  sendCloseStoreKit,
  sendCreativeClientClick,
  sendCreativeLoadError,
  sendCreativeVideoErrorVastDownload,
  sendCreativeViewableTrack,
  sendErrorStoreKit,
  sendImpressionFormat,
  sendMonitoringVideoCompleteEvent,
  sendMonitoringVideoStartedEvent,
  sendMonitoringVideoVastBlockingEvent,
  sendOpenStoreKit,
  sendTrack,
  waitUntilTracksAreSent,
  getAnalyticsService
} from '../../../common/js/TrackAnalyticsHelper';
import { EndScreenOrchestrator } from './end-screen/orchestrator';
import { VideoTrackingService } from './video-tracking-service';
import { logDiagnostic } from './diagnostic';
import {
  FetchError,
  NetworkFetchError,
  TimeoutFetchError
} from '@ogury/vast-library/common/utils/fetch-error';
import { canUseAsyncLoading } from './video-loader';
import { encapsulateClickUrlIfNeeded } from '../../../common/js/click-utils';
import { VPAIDViewMode } from '../../../@types/vpaid.type';
import { RootStore } from '../../../common/js/stores/root.store';
import {
  getLastProgress,
  getIsCompleted,
  getFullVideoIsCompleted,
  markAsCompleted,
  initializeQuartileCallbacks
} from './video-progress-service';

export interface Actions {
  onVideoEvent: (event: string) => void;
  onClick: (...args: any[]) => void;
  onOptout: (...args: any[]) => void;
  handleOgyOnCloseSystem: (...args: any[]) => void;
  onError: (...args: any[]) => void;
  onClose: (...args: any[]) => void;
  [key: string]: any;
}

const mwebCloseDelay = 1000;

let billedDuration: number;
let fullVideoDuration: number;
let videoTrackParameters: Record<string, any> | null;
let isVpaid = false;
let closeAlreadyDone = false;
let lastClickData: any = null;
let videoTrackers: Record<string, string> | null = null;
let isClosing = false;
let adDeliveryOptions: any = null;

const isOverlayThumbnail = (context?: AdContext) =>
  context?.adUnitType !== 'interstitial' &&
  isFeatureEnable('videoOverlayThumbnail');

export function setDeliveryOptions(adOptions: FormatOptions): void {
  adDeliveryOptions = adOptions;
}

export default function getActions(
  config: FormatConfig,
  adOptions: FormatOptions
): Actions {
  const videoStore = RootStore.getInstance().videoStore;
  const vast = videoStore.vast!;

  billedDuration = vast.getBilledDuration() || 0;
  fullVideoDuration = vast.getDuration() || 0;
  videoTrackParameters = getVideoTrackParameters(adOptions);
  isVpaid = videoStore.isVpaid;
  adDeliveryOptions = adOptions;

  // Initialize quartile callbacks with the event sender
  // This connects the video-progress-service to our tracking system
  initializeQuartileCallbacks(onEvent);

  const hasVideoTrackers =
    config.video?.trackers &&
    Object.keys(config.video.trackers).filter(k => config.video?.trackers?.[k])
      .length > 0;

  if (hasVideoTrackers && config.video?.trackers) {
    videoTrackers = { ...config.video.trackers };
  }

  return {
    handleOgyOnCloseSystem: handleOgyOnCloseSystem.bind(
      null,
      vast,
      adOptions.context
    ),
    onError: onError.bind(null, vast),
    onClick: preventSpamCallsShared(
      (
        url: string,
        source: string | undefined = undefined,
        handledByPlayer?: boolean
      ) => {
        onClick(
          vast,
          config,
          adOptions,
          adOptions.callbacks && adOptions.callbacks.onClick,
          source,
          url,
          handledByPlayer
        );
      }
    ),
    onClickVastIcon: (url: string) => {
      onClickVastIcon(adOptions, url);
    },
    onEvent,
    onAdStarted: onAdStarted.bind(null, vast),
    onVolumeChanged: (volume: number) => {
      onVolumeChanged(vast, volume);
    },
    onClose: onClose.bind(null, vast, adOptions.context),
    onStopped: onStopped.bind(null, adOptions.context),
    onOptout: preventSpamCallsShared(() => {
      openOptOut(adOptions);
      if (config.optOut?.closeAdOnClick) {
        onOptout(vast);
      }
    }),
    onForceClose,
    onToggleMute: onToggleMute.bind(null, vast),
    onVideoEvent: onVideoEvent.bind(null, config, adOptions.context),
    onToggleExpand,
    onComplete: onComplete.bind(null, vast),
    onImpression: onImpression.bind(null, vast),
    onResize,
    onPause: onPause.bind(null, vast),
    onResume: onResume.bind(null, vast),
    onSkip: onSkip.bind(null, vast, adOptions.context),
    skip: skip.bind(null, vast, adOptions.context)
  };
}

function handleVideoVastBlockError(err: any): void {
  if (typeof err === 'string' && err.indexOf('Ad blocked') > -1) {
    sendMonitoringVideoVastBlockingEvent();
  }
}

function handleVideoAutoplayError(err: any, callback: () => void): void {
  if (
    isWeb() &&
    typeof err.message !== 'undefined' &&
    err.message.indexOf('does not support autoplay') > -1
  ) {
    RootStore.getInstance().adContainerStore.adContainerPort.ogyVideoAutoplayError();
    return;
  }
  callback();
}

export function onError(
  vast: VASTClient | null,
  err: Error & { code?: number; additionalTrace?: any },
  shouldLogError: boolean = true,
  context?: AdContext
): void {
  sendCreativeLoadError();

  if (vast && err instanceof VASTError) {
    sendCreativeVideoErrorVastDownload(err);
    vast.onError(err);
  }

  const videoStore = RootStore.getInstance().videoStore;

  let diagnosticBody: Record<string, any> = {
    origin: 'onError',
    errorCode: err.code,
    errorMsg: err.message,
    errorObject: err,
    isClosing,
    fetchedRawVasts: vast ? vast.getFetchedRawVasts() : [],
    isVastInstanceNull: vast == null,
    shouldBeVastDownloadError: vast != null && err instanceof VASTError,
    shouldBeVastFetchError: err instanceof FetchError,
    shouldBeVastNetworkFetchError: err instanceof NetworkFetchError,
    shouldBeVastTimeoutFetchError: err instanceof TimeoutFetchError,
    userAgent: navigator.userAgent || 'undefined',
    ...(context && { canUseAsyncLoading: canUseAsyncLoading(context) }),
    isVpaid: isVpaid,
    isMuted: videoStore.videoPlayer?.value?.muted || false,
    vpaidState: vpaid.getVpaidState(),
    ...getDiagnosticVideoSources()
  };

  if (err.additionalTrace) {
    if (err.additionalTrace.fetchedRawVasts) {
      delete diagnosticBody.fetchedRawVasts;
    }

    diagnosticBody = {
      ...diagnosticBody,
      ...err.additionalTrace
    };
  }

  const isAutoPlayErrorInWeb =
    isWeb() &&
    typeof err.message !== 'undefined' &&
    err.message.indexOf('does not support autoplay') > -1;

  adDeliveryOptions &&
    !isAutoPlayErrorInWeb &&
    logDiagnostic(diagnosticBody, adDeliveryOptions);

  console.error(err);

  shouldLogError && logError(err);

  sendVideoTrack(formatEvent({ event: 'error' }));

  handleVideoVastBlockError(err);
  handleVideoAutoplayError(err, () => {
    setTimeout(() => {
      // Wait 500ms before killing the webview to let tracks be sent.
      RootStore.getInstance().adContainerStore.unloadFormat();
    }, 500);
  });
}

function getDiagnosticVideoSources(): Record<string, any> {
  const videoElement = document.getElementById(
    'video'
  ) as HTMLVideoElement | null;

  if (!videoElement) {
    return {};
  }

  const sources = document.querySelector('#video > source');

  const sourceSrcs: Array<{ src: string; type: string }> = [];

  if (sources) {
    for (let i = 0; i < (sources as any).length; i++) {
      sourceSrcs.push({
        src: (sources as any)[i].src,
        type: (sources as any)[i].type
      });
    }
  }

  return {
    videoSrc: videoElement.src,
    videoSourcesSrcs: sourceSrcs,
    readyState: videoElement.readyState,
    networkState: videoElement.networkState,
    currentSrc: videoElement.currentSrc,
    currentTime: videoElement.currentTime,
    crossOrigin: videoElement.crossOrigin
  };
}

export function onEvent(event: string): void {
  sendVideoTrack(event);
}

export function onClickVastIcon(adOptions: FormatOptions, url: string): void {
  RootStore.getInstance().adContainerStore.openBrowser(url, true);
}

export function onClick(
  vast: VASTClient,
  config: FormatConfig,
  adOptions: FormatOptions,
  onClickCallback: ((context: AdContext) => void) | undefined = () => {},
  source?: string,
  overrideUrl: string = '',
  openUrl?: boolean
): void {
  openUrl = !!(openUrl === undefined || openUrl === true);

  sendOmidMediaEvent('adUserInteraction', 'click');
  sendCreativeClientClick();
  sendCreativeViewableTrack();
  onClickCallback && onClickCallback(adOptions.context);

  try {
    let url: string | null = null;

    if (source === 'endScreen') {
      url = EndScreenOrchestrator.instance.getClickUrl();
    }

    if (url == null) {
      url = vast.click(overrideUrl);
    }

    url = templateMacros(
      url || '',
      getTrackingUrlMacros(config.trackingUrlMacroConfig || {})
    );

    lastClickData = { config, adOptions, url, vast, openUrl };

    RootStore.getInstance().adContainerStore.adContainerPort.ogyOnAdClicked();

    if (source === 'skoverlay') {
      sendTrack(getRedirectUrl({ ...lastClickData, openUrl: false }));
      return;
    }

    if (
      RootStore.getInstance().adContainerStore.canOpenStoreKit(
        adOptions.context
      )
    ) {
      sendOpenStoreKit();
      sendTrack(getRedirectUrl({ ...lastClickData, openUrl: false }));
      registerCloseStoreKitEvent();
      RootStore.getInstance().adContainerStore.adContainerPort.ogyOpenStoreKit();
      return;
    }

    if (!adOptions.context.isUserAcquisition) {
      sendOmidMediaEvent('skipped');

      if (isRewardVideo()) {
        if (config.video && config.video.sendTracksOnClick) {
          onComplete(vast);
        }
      }
    }

    redirectOnClick(lastClickData);
  } catch (err) {
    logError(new Error(`Clicking action failed : ${err}`));
    onClose(vast);
    throw err;
  }
}

function registerCloseStoreKitEvent(): void {
  const adContainerStore = RootStore.getInstance().adContainerStore;

  adContainerStore.adContainerPort.addEventListener(
    'ogyCloseStoreKit',
    function closeStoreKit(errorCode?: number) {
      adContainerStore.adContainerPort.removeEventListener(
        'ogyCloseStoreKit',
        closeStoreKit
      );
      const hasErrorCode = errorCode != undefined;
      if (!hasErrorCode) {
        sendCloseStoreKit();
        return;
      }
      sendErrorStoreKit(errorCode);
      try {
        lastClickData.bypassTracks = true;
        redirectOnClick(lastClickData);
      } catch (err) {
        logError(
          new Error(`Fallback on click from close store kit failed : ${err}`)
        );
        onClose(lastClickData.vast);
        throw err;
      }
    }
  );
}

function redirectOnClick({
  config,
  adOptions,
  url,
  openUrl,
  bypassTracks
}: {
  config: FormatConfig;
  adOptions: FormatOptions;
  url: string;
  openUrl: boolean;
  bypassTracks?: boolean;
}): void {
  installShortcut(adOptions, config, {
    forceRedirectUrl: url
  });

  const redirectUrl = getRedirectUrl({ config, url, openUrl, bypassTracks });
  if (openUrl) {
    RootStore.getInstance().adContainerStore.openAd(redirectUrl);
  } else {
    sendTrack(redirectUrl);
  }

  if (config.shouldCallMraidCloseOnClick && isWeb()) {
    triggerMraidClose({ force: false, closeDelay: mwebCloseDelay });
  }
}

function getRedirectUrl({
  config,
  url,
  openUrl,
  bypassTracks
}: {
  config: FormatConfig;
  url: string;
  openUrl: boolean;
  bypassTracks?: boolean;
}): string {
  const params: Record<string, any> = {
    source: 'click'
  };

  if (bypassTracks) {
    params.bt = true;
  }

  if (!openUrl) {
    params.ot = true;
  }

  if (url) {
    params.force_redirect = openUrl ? url : 'https://www.page-expired.com';
  }

  let redirectUrl = updateQueryStringParameters(config.clickUrl || '', params);
  redirectUrl = encapsulateClickUrlIfNeeded(redirectUrl);

  return redirectUrl;
}

export function handleOgyOnCloseSystem(
  vast?: VASTClient,
  context?: AdContext
): void {
  const trackEvent = true;
  const showNextAd = false;
  onClose(vast, context, trackEvent, showNextAd);
}

export function triggerMraidClose({
  force,
  param,
  closeDelay
}: {
  force: boolean;
  param?: boolean;
  closeDelay?: number;
}): void {
  const adContainerStore = RootStore.getInstance().adContainerStore;

  const callback = () => {
    let promise: Promise<void> = Promise.resolve();
    if (closeDelay) {
      promise = new Promise(resolve => {
        setTimeout(resolve, closeDelay);
      });
    }

    return promise.then(() => {
      isClosing = false;
      if (force) {
        adContainerStore.adContainerPort.ogyForceClose();
        return;
      }
      if (typeof param === 'undefined') {
        adContainerStore.adContainerPort.close();
      } else {
        adContainerStore.adContainerPort.close(param);
      }
    });
  };

  waitUntilTracksAreSent()
    .then(() => callback())
    .catch(() => callback());
}

function onStopped(context?: AdContext, showNextAd = true): Promise<void> {
  let waitPromise = Promise.resolve();

  if (isVpaid) {
    waitPromise = vpaid.waitForOngoingTracksToFinish();
  }

  return waitPromise.then(function() {
    if (
      context &&
      context.adUnitType !== 'interstitial' &&
      context.adUnitType !== 'optin_video'
    ) {
      triggerMraidClose({ force: true });
    } else {
      triggerMraidClose({ force: false, param: showNextAd });
    }
  });
}

function onClose(
  vast?: VASTClient,
  context?: AdContext,
  trackEvent: boolean = true,
  showNextAd = true
): void {
  if (isClosing) {
    return;
  }
  isClosing = true;
  const close = () => {
    isVpaid && vpaid.stopAd();
    !isVpaid && onStopped(context, showNextAd);
  };

  if (!trackEvent || !vast) {
    close();
    return;
  }

  vast
    .close()
    .then(() => {
      close();
    })
    .catch(() => {
      close();
    });
}

export function onReward(): void {
  RootStore.getInstance().adContainerStore.adContainerPort.ogyOnAdEvent(
    'rewards'
  );
}

function onForceClose(): void {
  isVpaid && vpaid.stopAd();
  triggerMraidClose({ force: true });
}

function onToggleExpand(isExpanded: boolean): void {
  const adContainerStore = RootStore.getInstance().adContainerStore;

  if (isExpanded) {
    adContainerStore.adContainerPort.close();
    sendCollapseTrack();
  } else {
    adContainerStore.adContainerPort.expand();
    sendExpandTrack();
  }
}

export function sendExpandTrack(): void {
  onEvent('expand');
  sendOmidMediaEvent('playerStateChange', 'expanded');
}

export function sendCollapseTrack(): void {
  onEvent('collapse');
  sendOmidMediaEvent('playerStateChange', 'normal');
}

export function sendPauseTracks(): void {
  onEvent('pause');
  onEvent('overlaped');
  sendOmidMediaEvent('pause');
}

export function sendResumeTracks(): void {
  onEvent('resume');
  sendOmidMediaEvent('resume');
}

export function onOptout(vast: VASTClient): void {
  vast
    .close()
    .then(() => {
      triggerMraidClose({ force: false });
    })
    .catch(() => {
      triggerMraidClose({ force: false });
    });
}

function onToggleMute(vast: VASTClient, isMuted: boolean): void {
  const volume: number = isMuted ? 0 : 1;

  sendOmidMediaEvent('volumeChange', volume);
  isVpaid && vpaid.setAdVolume(volume);
  !isVpaid && onVolumeChanged(vast, volume);
}

function onResize(
  width: number,
  height: number,
  viewMode: VPAIDViewMode = 'normal'
): void {
  isVpaid && resizeAd(width, height, viewMode);
}

function onSkip(
  vast: VASTClient,
  context?: AdContext,
  shouldClose = true
): void {
  vast.skip();
  shouldClose && onClose(vast, context);
}

function skip(vast?: VASTClient, context?: AdContext): void {
  if (isVpaid) {
    if (vpaid.getAdSkippableState() === true) {
      vpaid.skipAd();
    } else {
      onSkip(vast!, context);
    }
  } else {
    onClose(vast, context);
  }
}

function onPause(vast: VASTClient): void {
  vast.pause();
}

function onResume(vast: VASTClient): void {
  vast.resume();
}

export function onVideoEvent(
  config: FormatConfig,
  context: AdContext,
  event: string
): void {
  let oguryEvents: Array<{ event: string; options?: Record<string, any> }> = [];

  const lastProgress = getLastProgress();
  const isCompleted = getIsCompleted();
  const fullVideoIsCompleted = getFullVideoIsCompleted();

  const viewRate = Math.min(
    Math.round((lastProgress / billedDuration) * 100),
    100
  );

  const viewRateFullVideoDuration = Math.min(
    Math.round((lastProgress / fullVideoDuration) * 100),
    100
  );

  switch (event) {
    case 'closeLinear':
    case 'close':
      if (!closeAlreadyDone) {
        closeAlreadyDone = true;
        if (!isCompleted) {
          sendOmidMediaEvent('skipped');
          oguryEvents = [
            { event: 'closeLinear', options: { viewRate: viewRate } }
          ];
        }

        oguryEvents = [
          ...oguryEvents,
          {
            event: 'continue',
            options: !fullVideoIsCompleted ? { viewRateFullVideoDuration } : {}
          }
        ];
      }
      break;
    case 'creativeView':
      oguryEvents = [{ event: 'impression' }];
      triggerOguryVideoTrackers('impression');
      break;
    case 'start':
      sendImpressionFormat(RootStore.getInstance().adContainerStore);
      sendOmidImpression();
      sendOmidMediaEvent(
        'start',
        fullVideoDuration,
        config.video?.soundOnStart ? 1 : 0
      );
      sendOmidMediaEvent(
        'playerStateChange',
        isOverlayThumbnail(context) ? 'normal' : 'fullscreen'
      );
      sendMonitoringVideoStartedEvent();
      oguryEvents = [{ event: 'start' }, { event: 'firstSecond' }];
      if (isRewardVideo() && isRewardOnLaunch()) {
        onReward();
      }

      triggerOguryVideoTrackers('start');

      break;
    case 'complete':
      sendOmidMediaEvent('complete');
      sendMonitoringVideoCompleteEvent();
      oguryEvents = [{ event: 'complete', options: { viewRate: 100 } }];
      if (isRewardVideo() && !isRewardOnLaunch()) {
        onReward();
      }

      triggerOguryVideoTrackers('complete');

      break;
    default:
      // quartiles
      oguryEvents = [{ event: event }];
      sendOmidMediaEvent(event);
      triggerOguryVideoTrackers(event);

      break;
  }

  oguryEvents
    .filter(x => x !== null)
    .map(formatEvent)
    .forEach(sendVideoTrack);
}

function triggerOguryVideoTrackers(trackName: string): void {
  if (videoTrackers && videoTrackers[trackName]) {
    new Image().src = videoTrackers[trackName];
    delete videoTrackers[trackName];
  }
}

export function onComplete(vast: VASTClient): void {
  if (getIsCompleted()) {
    return;
  }

  markAsCompleted();
  sendAllTracks(vast);
  vast.complete();
}

export function onAdStarted(vast: VASTClient): void {
  vast.creativeView();
}

export function onVolumeChanged(vast: VASTClient, volume: number): void {
  vast.setMuted(!volume);
}

export function onImpression(vast: VASTClient): void {
  vast.impression();
}

export function sendAllTracks(vast: VASTClient): void {
  // Set progress to billed duration to ensure all tracking events fire
  vast.setProgress(vast.getBilledDuration());
}

export function isRewardVideo(): boolean {
  if (!isFeatureEnable('videoReward')) {
    return false;
  }

  const adUnit = (window as any).MRAID_ENV?.adUnit;

  return adUnit && adUnit.type === 'optin_video';
}

export function isRewardOnLaunch(): boolean {
  if (!isFeatureEnable('videoReward')) {
    return false;
  }

  const adUnit = (window as any).MRAID_ENV?.adUnit;
  return adUnit && adUnit.reward && adUnit.reward.launch === 'launch';
}

function getVideoTrackParameters(
  adOptions: FormatOptions
): Record<string, any> | null {
  if (!adOptions || !adOptions.context) {
    return null;
  }

  return {
    videoTrackingUrl: generateUrlWithRegion(
      CONFIG.VIDEO_TRACKING_URL,
      adOptions.context.region
    ),
    userId: adOptions.context.userId
  };
}

function sendVideoTrack(event: string): void {
  if ((window as any).isOgyPreview || !videoTrackParameters) {
    return;
  }

  if (videoTrackParameters.dsp === 'beeswax') {
    return;
  }

  const { videoTrackingUrl, userId } = videoTrackParameters;
  const queryParams = getAnalyticsService()?.getCommonQueryParameters();

  if (!videoTrackingUrl || !userId) {
    return;
  }

  new VideoTrackingService()
    .send(videoTrackingUrl, {
      ...queryParams,
      e: event
    })
    .catch(e => {
      logError(e);
      console.log(e);
    });
}
